import { useEffect } from 'react';

import { useParams } from '@tanstack/react-router';
import { Tooltip, TooltipContent, TooltipTrigger } from 'cpool-components/general/Tooltip';
import { LOAN_REQUEST_OFFER_SOURCES } from 'cpool-constants';
import type { SchemaLoanRequestResolver_GetAcceptedOffers } from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';
import { LuLandmark } from 'react-icons/lu';

import { OfferInfoDialog } from '../OfferInfoDialog';
import TreatButton from '~/components/TreatButton';
import type { OfferEntry } from '~/features/leadDetails/components/BankCreditRequest/OfferHistory/schemas/offers.schema';
import { LEAD_DETAILS_OVERLAYS } from '~/features/leadDetails/constants/overlays';
import useLead from '~/features/leadDetails/hooks/useLead';
import { useLeadDetailsOverlayState } from '~/features/leadDetails/hooks/useLeadDetailsOverlayState';

type Props = {
    withLabel?: boolean;
    offer: SchemaLoanRequestResolver_GetAcceptedOffers[number] | OfferEntry;
    routeControlled?: boolean;
};

export const Info = ({ offer, routeControlled = false, withLabel = false }: Props) => {
    const { t } = useTranslation();
    const { leadId } = useParams({ strict: false });
    const { data: lead } = useLead(leadId);
    const { isOpen, setOpen } = useLeadDetailsOverlayState({
        isRouteControlled: routeControlled,
        key: LEAD_DETAILS_OVERLAYS.OFFER_INFO_ID,
        value: offer.id,
    });

    const { source: offerSource } = offer;

    const disabled =
        ((offerSource === LOAN_REQUEST_OFFER_SOURCES.EUROPACE ||
            offerSource === LOAN_REQUEST_OFFER_SOURCES.EUROPACE_SYNC) &&
            !lead?.europace?.id) ||
        offerSource === LOAN_REQUEST_OFFER_SOURCES.MANUAL;
    const canEvaluateDisabledState = offerSource === LOAN_REQUEST_OFFER_SOURCES.MANUAL || lead !== undefined;

    useEffect(() => {
        if (routeControlled && isOpen && canEvaluateDisabledState && disabled) setOpen(false);
    }, [canEvaluateDisabledState, disabled, isOpen, routeControlled, setOpen]);

    const buttonContent = (
        <TreatButton
            onClick={(event) => {
                event.stopPropagation();
                setOpen(true);
            }}
            className={
                'flex items-center justify-center gap-2 rounded p-1.5 text-midnight hover:bg-surface-light transition-colors disabled:opacity-60 disabled:cursor-not-allowed'
            }
            disabled={disabled}
        >
            <LuLandmark size={16} data-testid="info-icon" />
            {withLabel && <span className="text-xs px-3">{t('OFFER_MANAGEMENT.ICONS_TOOLTIP.INFO')}</span>}
        </TreatButton>
    );

    return (
        <>
            {withLabel ? (
                buttonContent
            ) : (
                <Tooltip>
                    <TooltipTrigger>{buttonContent}</TooltipTrigger>
                    <TooltipContent>{t('OFFER_MANAGEMENT.ICONS_TOOLTIP.INFO')}</TooltipContent>
                </Tooltip>
            )}
            {!routeControlled && <OfferInfoDialog isOpen={isOpen} offer={offer} onClose={() => setOpen(false)} />}
        </>
    );
};
